// services/surveyService.ts - PostHog Survey Utilities
// PostHog is attached to `window.posthog`; typing lives in src/types/globals.d.ts.

interface ConditionalLogic {
	dependsOn: string; // Question ID this question depends on
	showWhen: string | string[]; // Value(s) that trigger showing this question
}

export interface SurveyQuestion {
	id: string;
	type: string;
	question: string;
	description?: string;
	optional: boolean;
	choices?: string[];
	scale?: number;
	display?: string;
	lowerBoundLabel?: string;
	upperBoundLabel?: string;
	shuffleOptions?: boolean;
	buttonText?: string;
	hasOpenChoice?: boolean;
	descriptionContentType?: string;
	skipSubmitButton?: boolean;
	conditional?: ConditionalLogic; // Optional conditional display logic
}

export interface SurveyData {
	id: string;
	name: string;
	description: string;
	type?: string;
	linked_flag_key?: string | null;
	targeting_flag_key?: string | null;
	questions: SurveyQuestion[];
	conditions?: unknown;
	start_date?: string | null;
	end_date?: string | null;
}

let surveyRespondentEmail: string | null = null;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SURVEY_RESPONDENT_EMAIL_STORAGE_KEY = "surveyRespondentEmail";
const POSTHOG_SURVEY_LOAD_TIMEOUT_MS = 3000;
/** After completing the survey, suppress CTA / thank-you gate for this many days. */
export const SURVEY_COMPLETED_SUPPRESS_DAYS = 30;

export function fetchPostHogSurvey(
	surveyId: string,
): Promise<SurveyData | null> {
	const posthog = window.posthog;
	const getSurveys = posthog?.getSurveys;
	if (!posthog || !getSurveys) {
		return Promise.resolve(null);
	}

	return new Promise((resolve) => {
		let completed = false;
		const timeoutId = window.setTimeout(() => {
			completed = true;
			resolve(null);
		}, POSTHOG_SURVEY_LOAD_TIMEOUT_MS);

		const finish = (survey: SurveyData | null): void => {
			if (completed) return;
			completed = true;
			window.clearTimeout(timeoutId);
			resolve(survey);
		};

		try {
			getSurveys.call(
				posthog,
				(surveys) => {
					finish(surveys.find((survey) => survey.id === surveyId) ?? null);
				},
				true,
			);
		} catch (error) {
			console.warn("Unable to load survey from PostHog", error);
			finish(null);
		}
	});
}

function surveyCompletedStorageKey(surveyId: string): string {
	return `hasCompletedSurvey_${surveyId}`;
}

function persistSurveyRespondentEmail(email: string): void {
	try {
		sessionStorage.setItem(SURVEY_RESPONDENT_EMAIL_STORAGE_KEY, email);
	} catch {
		/* ignore blocked storage */
	}
}

function readPersistedSurveyRespondentEmail(): string | null {
	try {
		const stored = sessionStorage.getItem(SURVEY_RESPONDENT_EMAIL_STORAGE_KEY);
		if (!stored) return null;
		const normalised = stored.trim().toLowerCase();
		return EMAIL_PATTERN.test(normalised) ? normalised : null;
	} catch {
		return null;
	}
}

function clearPersistedSurveyRespondentEmail(): void {
	try {
		sessionStorage.removeItem(SURVEY_RESPONDENT_EMAIL_STORAGE_KEY);
	} catch {
		/* ignore blocked storage */
	}
}

/**
 * Read email from the survey link. Prefer the URL hash (`#email=`) so the
 * address is not sent in server logs / Referer headers. Still accepts a
 * legacy `?email=` query for older emails. Clears both from the URL after read.
 */
export function applySurveyEmailFromUrl(): string | null {
	const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ""));
	const queryParams = new URLSearchParams(window.location.search);

	const email = (hashParams.get("email") || queryParams.get("email") || "")
		.trim()
		.toLowerCase();

	if (!email || !EMAIL_PATTERN.test(email)) {
		return null;
	}

	surveyRespondentEmail = email;
	persistSurveyRespondentEmail(email);

	if (window.posthog?.identify) {
		window.posthog.identify(email, { email });
	}

	hashParams.delete("email");
	queryParams.delete("email");
	const query = queryParams.toString();
	const hash = hashParams.toString();
	const cleanUrl =
		window.location.pathname +
		(query ? `?${query}` : "") +
		(hash ? `#${hash}` : "");
	history.replaceState({}, "", cleanUrl);

	return email;
}

export function getSurveyRespondentEmail(): string | null {
	if (surveyRespondentEmail) return surveyRespondentEmail;
	const persisted = readPersistedSurveyRespondentEmail();
	if (persisted) {
		surveyRespondentEmail = persisted;
	}
	return surveyRespondentEmail;
}

/**
 * Whether the survey was completed within the suppression window (default 30 days).
 * Legacy value `"true"` (no timestamp) is treated as still suppressed.
 */
export function hasCompletedSurveyRecently(
	surveyId: string,
	withinDays: number = SURVEY_COMPLETED_SUPPRESS_DAYS,
): boolean {
	try {
		const raw = localStorage.getItem(surveyCompletedStorageKey(surveyId));
		if (!raw) return false;
		if (raw === "true") return true; // legacy permanent flag
		const completedAt = parseInt(raw, 10);
		if (Number.isNaN(completedAt)) return false;
		const daysSince = (Date.now() - completedAt) / (1000 * 60 * 60 * 24);
		return daysSince < withinDays;
	} catch {
		return false;
	}
}

/** @deprecated Prefer hasCompletedSurveyRecently — kept for call sites expecting a boolean gate. */
export function hasCompletedSurvey(surveyId: string): boolean {
	return hasCompletedSurveyRecently(surveyId);
}

/**
 * Mark a survey as completed (stores completion timestamp for 30-day CTA suppression).
 */
export function markSurveyCompleted(surveyId: string): void {
	try {
		localStorage.setItem(
			surveyCompletedStorageKey(surveyId),
			Date.now().toString(),
		);
	} catch {
		/* ignore blocked storage */
	}
	surveyRespondentEmail = null;
	clearPersistedSurveyRespondentEmail();
	console.log(`Survey ${surveyId} marked as completed`);
}

/**
 * Capture survey shown event
 * @param surveyId - The PostHog survey ID
 */
export function captureSurveyShown(surveyId: string): void {
	if (window.posthog?.capture) {
		window.posthog.capture("survey shown", {
			$survey_id: surveyId,
		});
		console.log(`Survey shown event captured for ${surveyId}`);
	} else {
		console.warn("PostHog not available to capture survey shown event");
	}
}

/**
 * Capture survey sent event with responses
 * @param surveyId - The PostHog survey ID
 * @param responses - Object with question IDs as keys and responses as values
 */
export function captureSurveySent(
	surveyId: string,
	responses: Record<string, any>,
): void {
	if (window.posthog?.capture) {
		const eventData: Record<string, any> = {
			$survey_id: surveyId,
			...responses,
		};

		const respondentEmail = getSurveyRespondentEmail();
		if (respondentEmail) {
			eventData.email = respondentEmail;
		}

		window.posthog.capture("survey sent", eventData);
		console.log(`Survey sent event captured for ${surveyId}`, eventData);
	} else {
		console.warn(
			"PostHog not available to capture survey sent event. Storing locally.",
		);
		// Store locally for potential sync later
		const storageKey = `pendingSurveyResponse_${surveyId}`;
		localStorage.setItem(
			storageKey,
			JSON.stringify({ surveyId, responses, timestamp: Date.now() }),
		);
	}
}

/**
 * Shuffle an array (Fisher-Yates algorithm)
 */
function shuffleArray<T>(array: T[]): T[] {
	const shuffled = [...array];
	for (let i = shuffled.length - 1; i > 0; i--) {
		const j = Math.floor(Math.random() * (i + 1));
		[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
	}
	return shuffled;
}

export function escapeSurveyHtml(value: string): string {
	return value
		.replaceAll("&", "&amp;")
		.replaceAll("<", "&lt;")
		.replaceAll(">", "&gt;")
		.replaceAll('"', "&quot;")
		.replaceAll("'", "&#039;");
}

function escapedSelectorValue(value: string): string {
	return CSS.escape(value);
}

/**
 * Render a single choice question
 */
function renderSingleChoiceQuestion(
	question: SurveyQuestion,
	index: number,
): string {
	const choices = question.shuffleOptions
		? shuffleArray(question.choices || [])
		: question.choices || [];
	const required = !question.optional ? "required" : "";
	const questionId = escapeSurveyHtml(question.id);
	const openChoice = question.hasOpenChoice
		? choices.find((choice) => choice.toLowerCase() === "other")
		: undefined;

	return `
		<div class="survey-question mb-4" data-question-id="${questionId}">
			<label class="form-label fw-semibold">
				${escapeSurveyHtml(question.question)}
				${!question.optional ? '<span class="text-danger ms-1">*</span>' : ""}
			</label>
			${
				question.description
					? `<p class="text-white-50 small">${escapeSurveyHtml(question.description)}</p>`
					: ""
			}
			<div class="survey-choices d-flex flex-column gap-2">
				${choices
					.map(
						(choice) => `
					<div class="form-check">
						<input class="form-check-input" type="radio" name="question_${questionId}" value="${escapeSurveyHtml(choice)}" data-question-id="${questionId}" ${required} />
						<label class="form-check-label">${escapeSurveyHtml(choice)}</label>
					</div>
				`,
					)
					.join("")}
			</div>
			${
				openChoice
					? `<div class="survey-open-choice mt-2" hidden>
						<label class="visually-hidden" for="open_choice_${questionId}">Please specify</label>
						<input id="open_choice_${questionId}" class="form-control" type="text" data-open-choice-question-id="${questionId}" data-open-choice-value="${escapeSurveyHtml(openChoice)}" placeholder="Please specify" />
					</div>`
					: ""
			}
			<div class="survey-error text-danger mt-2" id="error_${questionId}"></div>
		</div>
	`;
}

/**
 * Render an open text question
 */
function renderOpenQuestion(question: SurveyQuestion, index: number): string {
	const required = !question.optional ? "required" : "";
	const questionId = escapeSurveyHtml(question.id);

	return `
		<div class="survey-question mb-4" data-question-id="${questionId}">
			<label class="form-label fw-semibold" for="question_${questionId}">
				${escapeSurveyHtml(question.question)}
				${!question.optional ? '<span class="text-danger ms-1">*</span>' : ""}
			</label>
			${
				question.description
					? `<p class="text-white-50 small">${escapeSurveyHtml(question.description)}</p>`
					: ""
			}
			<textarea 
				id="question_${questionId}"
				name="question_${questionId}"
				data-question-id="${questionId}"
				class="form-control"
				rows="4"
				placeholder="Type your response..."
				${required}
			></textarea>
			<div class="survey-error text-danger mt-2" id="error_${questionId}"></div>
		</div>
	`;
}

/**
 * Render a rating question
 */
function renderRatingQuestion(question: SurveyQuestion, index: number): string {
	const scale = question.scale || 10;
	const required = !question.optional ? "required" : "";
	const questionId = escapeSurveyHtml(question.id);

	return `
		<div class="survey-question mb-4" data-question-id="${questionId}">
			<label class="form-label fw-semibold">
				${escapeSurveyHtml(question.question)}
				${!question.optional ? '<span class="text-danger ms-1">*</span>' : ""}
			</label>
			${
				question.description
					? `<p class="text-white-50 small">${escapeSurveyHtml(question.description)}</p>`
					: ""
			}
			<div class="d-flex align-items-center gap-3 flex-wrap">
				${
					question.lowerBoundLabel
						? `<span class="text-white-70 small">${escapeSurveyHtml(question.lowerBoundLabel)}</span>`
						: ""
				}
				<div class="d-flex flex-wrap gap-2">
					${Array.from(
						{ length: scale + 1 },
						(_, i) => `
						<div class="form-check">
							<input class="form-check-input" type="radio" name="question_${questionId}" value="${i}" data-question-id="${questionId}" ${required} />
							<label class="form-check-label">${i}</label>
						</div>
					`,
					).join("")}
				</div>
				${
					question.upperBoundLabel
						? `<span class="text-white-70 small">${escapeSurveyHtml(question.upperBoundLabel)}</span>`
						: ""
				}
			</div>
			<div class="survey-error text-danger mt-2" id="error_${questionId}"></div>
		</div>
	`;
}

/**
 * Render a survey form
 * @param surveyData - The survey configuration
 * @param containerId - The ID of the container element
 */
export function renderSurveyForm(
	surveyData: SurveyData,
	containerId: string,
): void {
	const container = document.getElementById(containerId);
	if (!container) {
		console.error(`Container #${containerId} not found`);
		return;
	}

	// Build the form HTML
	const formHTML = `
        <div class="container p-0">
            <div class="card dashboard-card mb-3">
                <div class="card-body">
                    <form id="survey-form" class="custom-survey-form" novalidate>
                        ${
																									surveyData.description
																										? `<p class=\"text-white-50 mb-4\">${escapeSurveyHtml(surveyData.description)}</p>`
																										: ""
																								}
						${surveyData.questions
							.map((question, index) => {
								const questionId = escapeSurveyHtml(question.id);
								// Check if this question has conditional logic
								const shouldHideInitially = question.conditional !== undefined;
								const hideStyle = shouldHideInitially ? ' style="display: none;"' : "";

								let questionHTML = "";
								switch (question.type) {
									case "single_choice":
										questionHTML = renderSingleChoiceQuestion(question, index);
										break;
									case "open":
										questionHTML = renderOpenQuestion(question, index);
										break;
									case "rating":
										questionHTML = renderRatingQuestion(question, index);
										break;
									default:
										questionHTML = `<p>Unknown question type: ${escapeSurveyHtml(question.type)}</p>`;
								}

								// Wrap with conditional visibility div, storing the question ID
								return `<div class=\"survey-question-wrapper\" data-question-index=\"${index}\" data-question-id=\"${questionId}\"${hideStyle}>${questionHTML}<hr class=\"border-secondary opacity-50 my-4\" /></div>`;
							})
							.join("")}
                        <div class="mt-2 d-grid">
							<button type="submit" class="btn btn-primary w-100 survey-submit-btn">Submit</button>
						</div>
					</form>
				</div>
			</div>
		</div>
	`;

	container.innerHTML = formHTML;

	// Capture "survey shown" event
	captureSurveyShown(surveyData.id);

	// Add form submission handler
	const form = document.getElementById("survey-form") as HTMLFormElement;
	if (form) {
		form.addEventListener("submit", (e) => handleSurveySubmit(e, surveyData));

		setupOpenChoiceInputs(form);
		setupConditionalLogic(form, surveyData);
	}
}

function setupOpenChoiceInputs(form: HTMLFormElement): void {
	form
		.querySelectorAll<HTMLInputElement>("[data-open-choice-question-id]")
		.forEach((openChoiceInput) => {
			const questionId = openChoiceInput.dataset.openChoiceQuestionId;
			const openChoiceValue = openChoiceInput.dataset.openChoiceValue;
			const container = openChoiceInput.closest<HTMLElement>(
				".survey-open-choice",
			);
			if (!questionId || !openChoiceValue || !container) return;

			const updateVisibility = (): void => {
				const selected = form.querySelector<HTMLInputElement>(
					`input[data-question-id="${escapedSelectorValue(questionId)}"]:checked`,
				);
				const isOpenChoice = selected?.value === openChoiceValue;
				container.hidden = !isOpenChoice;
				openChoiceInput.required = isOpenChoice;
				if (!isOpenChoice) openChoiceInput.value = "";
			};

			form
				.querySelectorAll<HTMLInputElement>(
					`input[data-question-id="${escapedSelectorValue(questionId)}"]`,
				)
				.forEach((input) => input.addEventListener("change", updateVisibility));
			updateVisibility();
		});
}

/**
 * Setup conditional logic for question branching
 * Dynamically handles questions that depend on other questions' answers
 */
function setupConditionalLogic(
	form: HTMLFormElement,
	surveyData: SurveyData,
): void {
	// Iterate through all questions to find ones with conditional logic
	surveyData.questions.forEach((question, questionIndex) => {
		if (!question.conditional) return;

		const { dependsOn, showWhen } = question.conditional;

		// Find the question wrapper for this conditional question
		const conditionalWrapper = form.querySelector(
			`.survey-question-wrapper[data-question-id="${escapedSelectorValue(question.id)}"]`,
		) as HTMLElement;

		if (!conditionalWrapper) return;

		// Get all input elements from the parent question
		const parentInputs = form.querySelectorAll(
			`input[data-question-id="${escapedSelectorValue(dependsOn)}"]`,
		);

		if (parentInputs.length === 0) {
			console.warn(
				`Conditional question "${question.id}" depends on "${dependsOn}" which was not found`,
			);
			return;
		}

		// Convert showWhen to array for consistent handling
		const triggerValues = Array.isArray(showWhen) ? showWhen : [showWhen];

		// Function to check if conditional question should be shown
		const updateVisibility = () => {
			let shouldShow = false;

			// Check if any selected value matches the trigger values
			parentInputs.forEach((input) => {
				const inputElement = input as HTMLInputElement;
				if (inputElement.type === "radio" || inputElement.type === "checkbox") {
					if (inputElement.checked && triggerValues.includes(inputElement.value)) {
						shouldShow = true;
					}
				} else if (
					inputElement.type === "text" ||
					inputElement.tagName === "TEXTAREA"
				) {
					if (triggerValues.includes(inputElement.value)) {
						shouldShow = true;
					}
				}
			});

			// Show or hide the conditional question
			if (shouldShow) {
				conditionalWrapper.style.display = "";
			} else {
				conditionalWrapper.style.display = "none";

				// Clear the conditional question's response when hidden
				clearQuestionResponse(form, question.id);
			}
		};

		// Add event listeners to all parent inputs
		parentInputs.forEach((input) => {
			input.addEventListener("change", updateVisibility);
			// For text inputs, also listen to input events
			if (
				(input as HTMLInputElement).type === "text" ||
				input.tagName === "TEXTAREA"
			) {
				input.addEventListener("input", updateVisibility);
			}
		});

		// Initial visibility check
		updateVisibility();
	});
}

/**
 * Clear all responses for a specific question
 */
function clearQuestionResponse(
	form: HTMLFormElement,
	questionId: string,
): void {
	// Clear radio/checkbox inputs
	const inputs = form.querySelectorAll(
		`input[data-question-id="${escapedSelectorValue(questionId)}"]`,
	);
	inputs.forEach((input) => {
		const inputElement = input as HTMLInputElement;
		if (inputElement.type === "radio" || inputElement.type === "checkbox") {
			inputElement.checked = false;
		} else {
			inputElement.value = "";
		}
	});

	// Clear textarea
	const textarea = form.querySelector(
		`textarea[data-question-id="${escapedSelectorValue(questionId)}"]`,
	) as HTMLTextAreaElement;
	if (textarea) {
		textarea.value = "";
	}

	// Clear error message
	const errorEl = document.getElementById(`error_${questionId}`);
	if (errorEl) {
		errorEl.textContent = "";
	}
}

/**
 * Handle survey form submission
 */
function handleSurveySubmit(event: Event, surveyData: SurveyData): void {
	event.preventDefault();

	const form = event.target as HTMLFormElement;
	let isValid = true;
	const responses: Record<string, any> = {};

	// Clear previous errors
	document.querySelectorAll(".survey-error").forEach((el) => {
		el.textContent = "";
	});

	// Collect and validate responses
	surveyData.questions.forEach((question, index) => {
		// Check if this question is currently visible
		const questionWrapper = form.querySelector(
			`.survey-question-wrapper[data-question-index="${index}"]`,
		) as HTMLElement;

		// Skip validation if question is hidden
		if (questionWrapper && questionWrapper.style.display === "none") {
			return;
		}

		const responseKey = `$survey_response_${question.id}`;

		if (question.type === "open") {
			const textarea = form.querySelector(
				`textarea[data-question-id="${escapedSelectorValue(question.id)}"]`,
			) as HTMLTextAreaElement;
			const value = textarea?.value.trim() || "";

			if (!question.optional && !value) {
				isValid = false;
				showError(question.id, "This field is required");
			} else if (value) {
				responses[responseKey] = value;
			}
		} else if (question.type === "single_choice" || question.type === "rating") {
			const selected = form.querySelector(
				`input[data-question-id="${escapedSelectorValue(question.id)}"]:checked`,
			) as HTMLInputElement;

			if (!question.optional && !selected) {
				isValid = false;
				showError(question.id, "Please select an option");
			} else if (selected) {
				const openChoiceInput = form.querySelector<HTMLInputElement>(
					`input[data-open-choice-question-id="${escapedSelectorValue(question.id)}"]`,
				);
				if (
					openChoiceInput &&
					selected.value === openChoiceInput.dataset.openChoiceValue
				) {
					const openChoiceResponse = openChoiceInput.value.trim();
					if (!openChoiceResponse) {
						isValid = false;
						showError(question.id, "Please specify your answer");
					} else {
						responses[responseKey] = openChoiceResponse;
					}
				} else {
					responses[responseKey] = selected.value;
				}
			}
		}
	});

	if (!isValid) {
		// Scroll to first error
		const firstError = document.querySelector(".survey-error:not(:empty)");
		if (firstError) {
			firstError.scrollIntoView({ behavior: "smooth", block: "center" });
		}
		return;
	}

	// Disable submit button
	const submitBtn = form.querySelector(
		".survey-submit-btn",
	) as HTMLButtonElement;
	if (submitBtn) {
		submitBtn.disabled = true;
		submitBtn.textContent = "Submitting...";
	}

	// Capture survey sent event with responses
	captureSurveySent(surveyData.id, responses);

	// Mark as completed
	markSurveyCompleted(surveyData.id);

	// Trigger success callback (will be handled by the page)
	const successEvent = new CustomEvent("surveySubmitted", {
		detail: { surveyId: surveyData.id, responses },
	});
	document.dispatchEvent(successEvent);
}

/**
 * Show validation error for a question
 */
function showError(questionId: string, message: string): void {
	const errorEl = document.getElementById(`error_${questionId}`);
	if (errorEl) {
		errorEl.textContent = message;
	}
}

/**
 * Check if PostHog is available and initialized
 * @returns true if PostHog is available
 */
export function isPostHogAvailable(): boolean {
	return !!window.posthog;
}
